import { SupabaseClient } from '@supabase/supabase-js';
import { 
  Category, 
  CreateCategoryData, 
  UpdateCategoryData,
  CategoryWithTemplates,
  TodoTemplate,
  CreateTodoTemplateData,
  UpdateTodoTemplateData,
  TodoTemplateWithItems,
  TemplateItem,
  CreateTemplateItemData,
  UpdateTemplateItemData
} from '../types/todo';

export class CategoriesService {
  constructor(private supabase: SupabaseClient) {}

  // Categories CRUD Operations
  async getCategories(): Promise<CategoryWithTemplates[]> {
    let query = this.supabase
      .from('categories')
      .select(`
        *,
        todo_templates(count)
      `)
      .order('created_at', { ascending: false });

    const { data, error } = await query;

    if (error) {
      throw new Error(`Failed to fetch categories: ${error.message}`);
    }

    return data?.map(category => ({
      ...category,
      templates_count: category.todo_templates?.[0]?.count || 0
    })) || [];
  }

  async getCategoryById(id: string): Promise<CategoryWithTemplates | null> {
    const { data, error } = await this.supabase
      .from('categories')
      .select(`
        *,
        todo_templates(*)
      `)
      .eq('id', id)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        return null; // Not found
      }
      throw new Error(`Failed to fetch category: ${error.message}`);
    }

    return {
      ...data,
      templates: data.todo_templates || [],
      templates_count: data.todo_templates?.length || 0
    };
  }

  async createCategory(categoryData: CreateCategoryData, userId: string): Promise<Category> {
    const { data, error } = await this.supabase
      .from('categories')
      .insert({
        ...categoryData,
        user_id: userId,
        is_public: categoryData.is_public || false
      })
      .select()
      .single();

    if (error) {
      throw new Error(`Failed to create category: ${error.message}`);
    }

    return data;
  }

  async updateCategory(id: string, categoryData: UpdateCategoryData): Promise<Category> {
    const { data, error } = await this.supabase
      .from('categories')
      .update({
        ...categoryData,
        updated_at: new Date().toISOString()
      })
      .eq('id', id)
      .select()
      .single();

    if (error) {
      throw new Error(`Failed to update category: ${error.message}`);
    }

    return data;
  }

  async deleteCategory(id: string): Promise<void> {
    // First check if category has templates
    const { data: templates } = await this.supabase
      .from('todo_templates')
      .select('id')
      .eq('category_id', id);

    if (templates && templates.length > 0) {
      throw new Error('Cannot delete category with existing templates');
    }

    const { error } = await this.supabase
      .from('categories')
      .delete()
      .eq('id', id)

    if (error) {
      throw new Error(`Failed to delete category: ${error.message}`);
    }
  }

  // Todo Templates CRUD Operations
  async getTemplatesByCategory(categoryId: string, userId: string): Promise<TodoTemplate[]> {
    const { data, error } = await this.supabase
      .from('todo_templates')
      .select('*')
      .eq('category_id', categoryId)
      .or(`user_id.eq.${userId},is_public.eq.true`)
      .order('created_at', { ascending: false });

    if (error) {
      throw new Error(`Failed to fetch templates: ${error.message}`);
    }

    return data || [];
  }

  async getTemplateById(id: string, userId: string): Promise<TodoTemplateWithItems | null> {
    const { data, error } = await this.supabase
      .from('todo_templates')
      .select(`
        *,
        categories(*),
        template_items(*)
      `)
      .eq('id', id)
      .or(`user_id.eq.${userId},is_public.eq.true`)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        return null;
      }
      throw new Error(`Failed to fetch template: ${error.message}`);
    }

    return {
      ...data,
      category: data.categories,
      items: data.template_items?.sort((a: TemplateItem, b: TemplateItem) => a.order_index - b.order_index) || [],
      items_count: data.template_items?.length || 0
    };
  }

  async createTemplate(templateData: CreateTodoTemplateData, userId: string): Promise<TodoTemplate> {
    const { data, error } = await this.supabase
      .from('todo_templates')
      .insert({
        ...templateData,
        user_id: userId,
        is_public: templateData.is_public || false
      })
      .select()
      .single();

    if (error) {
      throw new Error(`Failed to create template: ${error.message}`);
    }

    return data;
  }

  async updateTemplate(id: string, templateData: UpdateTodoTemplateData, userId: string): Promise<TodoTemplate> {
    const { data, error } = await this.supabase
      .from('todo_templates')
      .update({
        ...templateData,
        updated_at: new Date().toISOString()
      })
      .eq('id', id)
      .select()
      .single();

    if (error) {
      throw new Error(`Failed to update template: ${error.message}`);
    }

    return data;
  }

  async deleteTemplate(id: string, userId: string): Promise<void> {
    // Delete template items first
    await this.supabase
      .from('template_items')
      .delete()
      .eq('template_id', id);

    // Then delete the template
    const { error } = await this.supabase
      .from('todo_templates')
      .delete()
      .eq('id', id)
      .eq('user_id', userId);

    if (error) {
      throw new Error(`Failed to delete template: ${error.message}`);
    }
  }

  // Template Items CRUD Operations
  async getTemplateItems(templateId: string): Promise<TemplateItem[]> {
    const { data, error } = await this.supabase
      .from('template_items')
      .select('*')
      .eq('template_id', templateId)
      .order('order_index', { ascending: true });

    if (error) {
      throw new Error(`Failed to fetch template items: ${error.message}`);
    }

    return data || [];
  }

  async createTemplateItem(itemData: CreateTemplateItemData): Promise<TemplateItem> {
    const { data, error } = await this.supabase
      .from('template_items')
      .insert({
        ...itemData,
        priority: itemData.priority || 'medium',
        is_completed: false
      })
      .select()
      .single();

    if (error) {
      throw new Error(`Failed to create template item: ${error.message}`);
    }

    return data;
  }

  async updateTemplateItem(id: string, itemData: UpdateTemplateItemData): Promise<TemplateItem> {
    const { data, error } = await this.supabase
      .from('template_items')
      .update({
        ...itemData,
        updated_at: new Date().toISOString()
      })
      .eq('id', id)
      .select()
      .single();

    if (error) {
      throw new Error(`Failed to update template item: ${error.message}`);
    }

    return data;
  }

  async deleteTemplateItem(id: string): Promise<void> {
    const { error } = await this.supabase
      .from('template_items')
      .delete()
      .eq('id', id);

    if (error) {
      throw new Error(`Failed to delete template item: ${error.message}`);
    }
  }

  async reorderTemplateItems(templateId: string, itemIds: string[]): Promise<void> {
    const updates = itemIds.map((id, index) => ({
      id,
      order_index: index,
      updated_at: new Date().toISOString()
    }));

    const { error } = await this.supabase
      .from('template_items')
      .upsert(updates);

    if (error) {
      throw new Error(`Failed to reorder template items: ${error.message}`);
    }
  }

  // Utility methods
  async duplicateTemplate(templateId: string, userId: string, newName?: string): Promise<TodoTemplate> {
    // Get the original template with items
    const originalTemplate = await this.getTemplateById(templateId, userId);
    if (!originalTemplate) {
      throw new Error('Template not found');
    }

    // Create new template
    const newTemplate = await this.createTemplate({
      name: newName || `${originalTemplate.name} (Copy)`,
      description: originalTemplate.description || undefined,
      category_id: originalTemplate.category_id,
      is_public: false // Duplicated templates are private by default
    }, userId);

    // Copy all items
    if (originalTemplate.items && originalTemplate.items.length > 0) {
      const itemPromises = originalTemplate.items.map(item =>
        this.createTemplateItem({
          template_id: newTemplate.id,
          title: item.title,
          description: item.description || undefined,
          order_index: item.order_index,
          priority: item.priority
        })
      );

      await Promise.all(itemPromises);
    }

    return newTemplate;
  }

  async searchTemplates(query: string, userId: string): Promise<TodoTemplateWithItems[]> {
    const { data, error } = await this.supabase
      .from('todo_templates')
      .select(`
        *,
        categories(*),
        template_items(*)
      `)
      .or(`user_id.eq.${userId},is_public.eq.true`)
      .or(`name.ilike.%${query}%,description.ilike.%${query}%`)
      .order('created_at', { ascending: false });

    if (error) {
      throw new Error(`Failed to search templates: ${error.message}`);
    }

    return data?.map(template => ({
      ...template,
      category: template.categories,
      items: template.template_items?.sort((a: TemplateItem, b: TemplateItem) => a.order_index - b.order_index) || [],
      items_count: template.template_items?.length || 0
    })) || [];
  }
}
